'use client';
import {
  type Coach,
  CohortBanner,
  compact,
  isDateBefore,
  pluralize,
  SkuTier,
  Tooltip,
} from '@leland-dev/leland-ui-library';
import { isEmpty } from 'lodash-es';
import { useRouter } from 'next/compat/router';
import { useSearchParams } from 'next/navigation';
import React, {
  type Dispatch,
  type FC,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { type GoalName } from '../../__generated-gql-types__/globalTypes';
import Breadcrumbs, {
  type BreadcrumbsProps,
} from '../../components/breadcrumbs/Breadcrumbs';
import {
  CoachesTabs,
  type CoachesTabsDataProps,
} from '../../components/category/CoachesTabs';
import { useSchoolFilters } from '../../components/filters/SchoolFilters';
import {
  CoachesSectionWithFilters,
  SRP_PAGE_SIZE,
} from '../../components/search/CoachesSectionWithFilters';
import GetRecommendationsBanner from '../../components/search/GetRecommendationsBanner';
import { LowAvailabilitySrpContent } from '../../components/search/LowAvailabilitySrpContent';
import OfferingPackages from '../../components/search/OfferingPackages';
import { Round2CoachesBanner } from '../../components/search/Round2CoachesBanner';
import SRPSearchBar from '../../components/search/SRPSearchBar';
import { TalkToATeamMemberButton } from '../../components/search/TalkToATeamMemberButton';
import BookedCoachesCarousel from '../../components/search/carousels/BookedCoachesCarousel';
import { BrowsePopularCollegeSchools } from '../../components/search/carousels/BrowsePopularCollegeSchools';
import BrowseSubCategories from '../../components/search/carousels/BrowseSubCategories';
import UpcomingClassesCarousel, {
  type UpcomingClassesCarouselDataProps,
} from '../../components/search/carousels/UpcomingClassesCarousel';
import mxpnl from '../../utils/analytics/mixpanel';
import phog from '../../utils/analytics/posthog/posthog';
import { getSubCategoryTitle } from '../../utils/category';
import {
  __STAGING__,
  COLLEGE_CATEGORY_SLUG,
  MASTERS_PROGRAMS_URN,
  MBA_CATEGORY_SLUG,
} from '../../utils/constants';
import { formatGoalCoachesLabel } from '../../utils/formatters';
import { useComponentBoundingBox } from '../../utils/hooks/useComponentBoundingBox';
import useMediaQuery, { Breakpoints } from '../../utils/hooks/useMediaQuery';
import { getBatchResponseStart } from '../../utils/pagination';
import {
  convertQueryVariables,
  generateSearchPath,
  generateSearchQuery,
  getHourlyRateOptions,
  HOURLY_RATE_SUGGESTIONS_RADIO_VALUES,
  type SearchFiltersVariables,
} from '../../utils/search';
import { getUrlString } from '../../utils/url';

import {
  type CategoryLevelPageCategoryFragment,
  type CategoryLevelPageLelandPlusContributorCoachBatchResponseFragment,
  type CategoryLevelPageSchoolFragment,
  type CategoryLevelPageSearchCoachesQuery,
  type CategoryLevelPageSearchCoachesQueryVariables,
  type CategoryLevelPageSkuFragment,
  type CategoryLevelPageSubCategoryFragment,
  useCategoryLevelPageBookedCoachesQuery,
  useCategoryLevelPageSearchCoachesQuery,
} from './__generated-gql-types__/[[...subCategoryAndPage]].generated';

const ROUND_2_BANNER_CUTOFF_DATE = new Date('2025-01-09T12:00:00.000Z');

type SearchVariables = SearchFiltersVariables & { page?: number };

interface CohortBannerData {
  title: string;
  description: string;
  totalSessions: number;
  startAt: number;
  eventUrl: string;
  coaches: Coach[];
}

interface CategoryLevelPageProps {
  goal: GoalName;
  page: number;
  category: CategoryLevelPageCategoryFragment;
  subCategory: Nullable<CategoryLevelPageSubCategoryFragment>;
  coaches: Nullable<CategoryLevelPageSearchCoachesQuery>;
  upcomingClassesCarouselProps: UpcomingClassesCarouselDataProps;
  coachesTabProps: CoachesTabsDataProps;
  lelandPlusContributors: Nullable<CategoryLevelPageLelandPlusContributorCoachBatchResponseFragment>;
  bootcampInfo: Nullable<CohortBannerData>;
  skusData: CategoryLevelPageSkuFragment[];
}

const SECTION_SPACING_CLASSNAME = 'lg:mt-16 mt-10';

const CategoryLevelPage: FC<CategoryLevelPageProps> = ({
  goal,
  page: initialPage,
  category,
  subCategory,
  coaches: initialCoaches,
  upcomingClassesCarouselProps,
  coachesTabProps,
  lelandPlusContributors,
  bootcampInfo,
  skusData,
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const isSm = useMediaQuery(Breakpoints.SM);
  const coachSectionRef = useRef<HTMLDivElement>(null);
  const [popularSchoolsRef, popularSchoolsSize] = useComponentBoundingBox();

  const searchVariables: SearchFiltersVariables = useMemo(
    () =>
      generateSearchQuery({
        ...convertQueryVariables(searchParams),
        subCategory: subCategory ? subCategory.slug : null,
      }),
    [searchParams, subCategory],
  );

  const [page, setPage] = useState<number>(initialPage);

  const searchQuery = searchParams.get('keyword') as string | undefined;

  const goalTitle = `${formatGoalCoachesLabel(category.goal)}`;
  const categoryTitle = category ? `${category.name} ` : '';
  const subCategoryTitle = subCategory
    ? `${subCategory.name}${category.id == MASTERS_PROGRAMS_URN ? " Master's" : ''} `
    : '';
  const baseTitle = `${categoryTitle}${goalTitle}`;
  const defaultPageTitle = `${subCategoryTitle || categoryTitle}${goalTitle}`;
  const pageTitle = category.srpTitle || defaultPageTitle;

  useEffect(() => {
    setPage(initialPage);
    // on category/subcategory change, reset `page`
  }, [initialPage, category.id, subCategory?.id]);

  const [baseTrackingVars, trackingVarsWithSearchVars] = useMemo(() => {
    const baseVars = {
      categorySlug: category.slug,
      ...(subCategory?.slug ? { subCategorySlug: subCategory.slug } : {}),
    };
    return [baseVars, { ...baseVars, ...searchVariables }];
  }, [category.slug, subCategory?.slug, searchVariables]);

  const [isDirty, isDirtyIgnoreSubcategory] = useMemo(() => {
    const { goal, category, ...rest } = searchVariables;
    const { subCategory, ...restIgnoreSubCategory } = rest;
    return [!isEmpty(rest), !isEmpty(restIgnoreSubCategory)];
  }, [searchVariables]);

  const cleanSearchVariables = (vars: SearchVariables) => {
    const {
      goal,
      category,
      subCategory,
      page,
      shouldDeepRoute,
      preventScroll,
      ...rest
    } = vars;
    return rest;
  };

  const setSearchVariables: Dispatch<SetStateAction<SearchVariables>> =
    useCallback(
      (newVars) => {
        if (typeof newVars === 'function') {
          newVars = newVars(searchVariables);
        }

        const categoryToUse = category.slug;
        const subCategoryToUse = newVars.subCategory;
        const shallow = !newVars.shouldDeepRoute;
        const preventScroll = !!newVars.preventScroll;
        const pageToUse = newVars.page ?? 1;

        newVars = cleanSearchVariables(newVars);

        const cleanedNewVars = generateSearchQuery(newVars);

        const newUrl = getUrlString(
          generateSearchPath({
            goal: goal,
            categorySlug: categoryToUse,
            subCategorySlug: subCategoryToUse,
            page: pageToUse.toString(),
          }),
          cleanedNewVars,
        );

        setPage(pageToUse);
        void router?.push(newUrl, undefined, {
          scroll: !shallow,
          shallow,
        });

        if (shallow && !preventScroll && coachSectionRef.current) {
          if (cleanedNewVars.schoolsFilter && popularSchoolsSize) {
            const whereTo =
              window.scrollY +
              coachSectionRef.current.getBoundingClientRect().y -
              popularSchoolsSize.height;
            window.scrollTo({ top: whereTo, behavior: 'smooth' });
          } else {
            coachSectionRef.current.scrollIntoView({ behavior: 'smooth' });
          }
        }
      },
      [searchVariables, router, category.slug, goal, popularSchoolsSize],
    );

  const resetVariables = () => {
    schoolFiltersHookData.removeSchoolFilters(
      schoolFiltersHookData.selectedSchools.map((school) => school.id),
    );
    setSearchVariables({ shouldDeepRoute: !!subCategory?.id });
  };

  const setSchoolFilters = useCallback(
    (schools: CategoryLevelPageSchoolFragment[], preventScroll?: boolean) => {
      setSearchVariables((oldVariables) => ({
        ...oldVariables,
        schoolsFilter: schools.map(({ id }) => id),
        preventScroll,
      }));
    },
    [setSearchVariables],
  );

  const schoolFiltersHookData = useSchoolFilters({
    initialSelectedSchoolIds: searchVariables.schoolsFilter?.filter(compact),
    onChange: setSchoolFilters,
  });

  const cleanedSearchVariables: Omit<
    CategoryLevelPageSearchCoachesQueryVariables,
    'offset' | 'count'
  > = useMemo(() => {
    const cleanedVariables: Omit<
      CategoryLevelPageSearchCoachesQueryVariables,
      'offset' | 'count'
    > = {
      ...searchVariables,
      categoryUrn: category.id,
      subCategoryListFilter: subCategory?.id,
    };

    const { hourlyRateMin, hourlyRateMax } = searchVariables;
    if (hourlyRateMin != null || hourlyRateMax != null) {
      const skuTiers: CategoryLevelPageSearchCoachesQueryVariables['skuTiers'] =
        [];

      const hourlyRateFilterOptions = getHourlyRateOptions(
        hourlyRateMin,
        hourlyRateMax,
      );
      for (const [key, isChecked] of Object.entries(hourlyRateFilterOptions)) {
        if (!isChecked) continue;

        switch (key as HOURLY_RATE_SUGGESTIONS_RADIO_VALUES) {
          case HOURLY_RATE_SUGGESTIONS_RADIO_VALUES.LOW:
            skuTiers.push(SkuTier.BASE);
            break;
          case HOURLY_RATE_SUGGESTIONS_RADIO_VALUES.MEDIUM:
            skuTiers.push(SkuTier.PRO);
            break;
          case HOURLY_RATE_SUGGESTIONS_RADIO_VALUES.HIGH:
            skuTiers.push(SkuTier.ELITE, SkuTier.ELITE_PLUS);
            break;
        }
      }

      cleanedVariables.skuTiers = skuTiers;
    }

    cleanedVariables.categoryUrn = category.id;

    return cleanedVariables;
  }, [category.id, searchVariables, subCategory?.id]);

  const hasRealFilters = !isEmpty(cleanSearchVariables(searchVariables));

  const { data: bookedCoachesData } = useCategoryLevelPageBookedCoachesQuery();
  const bookedCoaches = bookedCoachesData?.bookedCoaches.results ?? [];

  const {
    data: clientCoaches,
    loading: clientCoachesLoading,
    error: coachesError,
  } = useCategoryLevelPageSearchCoachesQuery({
    variables: {
      ...cleanedSearchVariables,
      offset: getBatchResponseStart(page, SRP_PAGE_SIZE),
      count: SRP_PAGE_SIZE,
    },
    skip: !hasRealFilters && !!initialCoaches,
  });

  const coachesLoading =
    (!initialCoaches || hasRealFilters) && clientCoachesLoading;
  const coaches = clientCoaches ?? initialCoaches;

  const totalCoachResults = coaches?.searchCoaches.total ?? 0;

  const coachesSectionTitle =
    !coachesLoading && searchQuery
      ? `${pluralize(totalCoachResults, 'coach')} ${
          totalCoachResults === 1 ? 'matches' : 'match'
        } your search`
      : `All ${baseTitle}`;

  const renderSubtitleButton = useCallback(
    (resetVarName: keyof SearchFiltersVariables) => (
      <p className="mt-2 text-base font-medium text-leland-gray-extra-light">
        See all{' '}
        <button
          type="button"
          className="link link--primary font-medium"
          onClick={() =>
            setSearchVariables((oldVars) => {
              return {
                ...oldVars,
                [resetVarName]: null,
                shouldDeepRoute: resetVarName === 'subCategory',
              };
            })
          }
        >
          {baseTitle}
        </button>
      </p>
    ),
    [setSearchVariables, baseTitle],
  );

  const subtitleButton = subCategory
    ? renderSubtitleButton('subCategory')
    : null;

  const buildPaginationUrlString = useCallback(
    (page: Possible<number>) => {
      const subCategoryAndPage = searchParams?.get('subCategoryAndPage') ?? [];
      const { subCategory } = getSubCategoryAndPage(
        (subCategoryAndPage as string[]) || [],
      );
      return generateSearchPath({
        goal: goal,
        categorySlug: category.slug,
        subCategorySlug: subCategory,
        page: (page ?? 1).toString(),
      });
    },
    [searchParams, goal, category.slug],
  );

  const breadcrumbs: BreadcrumbsProps['breadcrumbs'] = useMemo(
    () =>
      [
        {
          label: category.name,
          url: generateSearchPath({
            goal: goal,
            categorySlug: category.slug,
            subCategorySlug: null,
          }),
        },
        subCategory
          ? {
              label: subCategory.name,
              url: generateSearchPath({
                goal,
                categorySlug: category.slug,
                subCategorySlug: subCategory.slug,
              }),
            }
          : null,
      ].filter(compact),
    [goal, category, subCategory],
  );

  const isLowAvailabilitySrp =
    // show if there are no real filters
    !hasRealFilters &&
    // and there are less than 7 coaches
    totalCoachResults < 7;

  return (
    <>
      <div className="size-full min-h-screen max-w-page bg-white px-4 py-6 sm:mx-auto sm:px-6 sm:py-10">
        <Breadcrumbs className="mb-6" breadcrumbs={breadcrumbs} />
        <div className="hidden w-full items-center justify-between lg:flex">
          <div className="flex flex-col items-start justify-center">
            <h1 className="text-3xl font-medium">{pageTitle}</h1>
            {subtitleButton}
          </div>
          <TalkToATeamMemberButton
            buttonId="SRP"
            baseTrackingVars={baseTrackingVars}
          />
        </div>

        {isLowAvailabilitySrp ? (
          <>
            <h2 className="mb-2 text-4xl font-medium text-leland-gray-dark lg:hidden">
              {pageTitle}
            </h2>
            <LowAvailabilitySrpContent
              baseTrackingVars={baseTrackingVars}
              subCategoryOrCategoryTitle={subCategoryTitle || categoryTitle}
            />
          </>
        ) : (
          <>
            <div className="flex w-full flex-col items-start lg:flex-row lg:justify-between">
              <div className="mb-6 flex flex-col items-start justify-center lg:hidden">
                <h1 className="text-4xl font-medium">{pageTitle}</h1>
                {subtitleButton}
              </div>
              <SRPSearchBar
                className="mb-2 flex w-full rounded-lg lg:mt-8 lg:rounded-full"
                inputContainerClassName="rounded-full py-1"
                value={searchVariables.keyword}
                placeholder="Search Leland coaching..."
                setSearchVariables={setSearchVariables}
                onFocus={() => {
                  mxpnl.track('SRP - click - Search Bar', baseTrackingVars);
                  phog.capture('srp--search_bar_focus', baseTrackingVars);
                }}
                onChange={(updatedKeyword) => {
                  if (updatedKeyword) {
                    mxpnl.track('SRP - filter - Search Bar', {
                      ...trackingVarsWithSearchVars,
                      updatedKeyword,
                    });
                    phog.capture('srp--search_bar_filter', {
                      ...trackingVarsWithSearchVars,
                      updatedKeyword,
                    });
                  }
                }}
              />
              <TalkToATeamMemberButton
                buttonId="SRP"
                className="w-full lg:hidden"
                baseTrackingVars={baseTrackingVars}
              />
            </div>

            {searchQuery ? null : (
              <>
                {category.slug === COLLEGE_CATEGORY_SLUG &&
                !searchParams.get('schoolsFilter') ? (
                  <div className="mt-12 w-full">
                    <BrowsePopularCollegeSchools
                      onClick={(updatedValue, label) => {
                        setSearchVariables((oldVars) => ({
                          ...oldVars,
                          schoolsFilter: updatedValue,
                          ff: label,
                        }));
                        mxpnl.track('SRP - click - Featured Filter', {
                          ...baseTrackingVars,
                          updatedValue,
                        });
                        phog.capture('srp--featured_filter_click', {
                          ...baseTrackingVars,
                          updatedValue,
                        });
                      }}
                      ref={popularSchoolsRef}
                    />
                  </div>
                ) : category.shouldShowSubCategories &&
                  !searchVariables.subCategory ? (
                  <div className="mt-12 w-full">
                    <BrowseSubCategories
                      subCategoryList={category.subCategoryList ?? []}
                      subCategoryTitle={getSubCategoryTitle(
                        category.subCategoryTitle,
                      )}
                      onClick={(updatedValue) => {
                        setSearchVariables((prev) => ({
                          ...prev,
                          subCategory: updatedValue,
                          shouldDeepRoute: true,
                        }));
                        mxpnl.track('SRP - click - Featured Filter', {
                          ...baseTrackingVars,
                          updatedValue,
                        });
                        phog.capture('srp--featured_filter_click', {
                          ...baseTrackingVars,
                          updatedValue,
                        });
                      }}
                    />
                  </div>
                ) : null}

                {category.slug === MBA_CATEGORY_SLUG &&
                isDateBefore(Date.now(), ROUND_2_BANNER_CUTOFF_DATE) ? (
                  <Round2CoachesBanner className={SECTION_SPACING_CLASSNAME} />
                ) : null}

                {coachesTabProps &&
                (totalCoachResults > 4 || bookedCoaches.length > 0) ? (
                  <CoachesTabs
                    className={SECTION_SPACING_CLASSNAME}
                    categoryUrn={category.id}
                    bookedCoaches={bookedCoaches}
                    initialData={coachesTabProps}
                    subCategoryListFilter={subCategory?.id}
                  />
                ) : (
                  <div className={SECTION_SPACING_CLASSNAME}>
                    <BookedCoachesCarousel
                      categoryUrn={category.id}
                      onClick={(coach, index) => {
                        mxpnl.track('SRP - click - Booked Coach', {
                          ...baseTrackingVars,
                          coachId: coach.id,
                          coachSlug: coach.slug,
                          index,
                        });
                        phog.capture('srp--booked_coach_click', {
                          ...baseTrackingVars,
                          coachId: coach.id,
                          coachSlug: coach.slug,
                          index,
                        });
                      }}
                    />
                  </div>
                )}

                <div className="mt-8">
                  <GetRecommendationsBanner trackingVars={baseTrackingVars} />
                </div>

                {bootcampInfo &&
                isDateBefore(Date.now(), bootcampInfo.startAt) ? (
                  <div className={SECTION_SPACING_CLASSNAME}>
                    <CohortBanner
                      title={bootcampInfo.title}
                      time={bootcampInfo.startAt}
                      description={bootcampInfo.description}
                      numSessions={bootcampInfo.totalSessions}
                      eventUrl={bootcampInfo.eventUrl}
                      coaches={bootcampInfo.coaches}
                      onClick={() => {
                        mxpnl.track('Cohort Banner Click', {
                          ...baseTrackingVars,
                          source: 'srp',
                          categorySlug: category.slug,
                        });
                        phog.capture('srp--cohort_banner_click', {
                          ...baseTrackingVars,
                          source: 'srp',
                          categorySlug: category.slug,
                        });
                      }}
                    />
                  </div>
                ) : null}
              </>
            )}

            {searchQuery ? null : (
              <div className={SECTION_SPACING_CLASSNAME}>
                <UpcomingClassesCarousel
                  title={
                    !isSm ? 'Events & classes' : 'Free events & group classes'
                  }
                  categories={[category]}
                  onClick={(c, index) => {
                    mxpnl.track('SRP - click - Class', {
                      ...baseTrackingVars,
                      index,
                      classId: c.classId,
                      classSlug: c.slug,
                      ...(c.upcomingSessionsV2?.length
                        ? { classSessionId: c.upcomingSessionsV2[0].id }
                        : {}),
                    });
                    phog.capture('srp--class_click', {
                      ...baseTrackingVars,
                      index,
                      classId: c.classId,
                      classSlug: c.slug,
                    });
                  }}
                  trackingPrefix="CategoryLevelPage"
                  initialData={upcomingClassesCarouselProps}
                />
              </div>
            )}

            <CoachesSectionWithFilters
              innerRef={coachSectionRef}
              spacingClassName={SECTION_SPACING_CLASSNAME}
              title={coachesSectionTitle}
              coaches={coaches?.searchCoaches.results ?? []}
              totalCoaches={coaches?.searchCoaches.total ?? 0}
              category={category}
              schoolFilters={schoolFiltersHookData}
              searchVariables={searchVariables}
              setSearchVariables={setSearchVariables}
              resetVariables={resetVariables}
              page={page}
              setPage={setPage}
              buildPaginationUrlString={buildPaginationUrlString}
              isDirty={isDirty}
              isDirtyIgnoreSubcategory={isDirtyIgnoreSubcategory}
              hasRealFilters={hasRealFilters}
              loading={coachesLoading}
              error={coachesError}
              baseTrackingVars={baseTrackingVars}
              trackingVarsWithSearchVars={trackingVarsWithSearchVars}
              // only show leland+ banner on first page
              showLelandPlusBanner={page === 1}
              lelandPlusContributors={lelandPlusContributors}
              skusData={skusData}
            />

            <OfferingPackages
              category={category}
              className={SECTION_SPACING_CLASSNAME}
              title="Browse popular coaching packages"
              subTitle={
                <>
                  All coaching packages include coach matching support, access
                  to{' '}
                  <Tooltip
                    className="underline"
                    content="Access a library of videos, templates, and examples curated by Leland's top coaches."
                  >
                    Leland+
                  </Tooltip>
                  , member discounts, exclusive events, and more.
                </>
              }
              subCategory={subCategory}
              hideFilters
              onClick={(p, index) => {
                mxpnl.track('SRP - click - Leland Package', {
                  ...baseTrackingVars,
                  index,
                  packageSlug: p.slug,
                  packageId: p.id,
                  packageName: p.name,
                  coachSlug: p.coach.slug,
                  coachId: p.coach.id,
                  ...(searchVariables.keyword
                    ? { keyword: searchVariables.keyword }
                    : {}),
                });
                phog.capture('srp--leland_package_click', {
                  ...baseTrackingVars,
                  index,
                  packageSlug: p.slug,
                  packageId: p.id,
                  packageName: p.name,
                });
              }}
              onClickViewMore={() => {
                mxpnl.track('SRP - click - View more packages', {
                  ...baseTrackingVars,
                  ...(searchVariables.keyword
                    ? { keyword: searchVariables.keyword }
                    : {}),
                });
                phog.capture('srp--view_more_packages_click', {
                  ...baseTrackingVars,
                  ...(searchVariables.keyword
                    ? { keyword: searchVariables.keyword }
                    : {}),
                });
              }}
            />
          </>
        )}
      </div>
    </>
  );
};

interface SubCategoryAndPage {
  subCategory: Nullable<string>;
  page: number;
}

const getSubCategoryAndPage = (
  subCategoryAndPageArray: string[],
): SubCategoryAndPage => {
  if (subCategoryAndPageArray.length === 0) {
    return {
      subCategory: null,
      page: 1,
    };
  }
  if (subCategoryAndPageArray.length === 2) {
    return {
      subCategory: subCategoryAndPageArray[0],
      page: parseInt(subCategoryAndPageArray[1], 10),
    };
  }
  if (subCategoryAndPageArray.length === 1) {
    const parsedValue = parseInt(subCategoryAndPageArray[0], 10);
    if (isNaN(parsedValue)) {
      return {
        subCategory: subCategoryAndPageArray[0],
        page: 1,
      };
    }
    return {
      subCategory: null,
      page: parsedValue,
    };
  }
  return {
    subCategory: null,
    page: 1,
  };
};

export default CategoryLevelPage;
